import React, { useRef } from "react";
import PropTypes from "prop-types";
import { uniqueId } from "lodash";
import { connect } from "react-redux";
import { withTranslation } from "react-i18next";
import { compose } from "redux";
import { motion } from "framer-motion";

import { actions as topikActions } from "../../../../modules/topik";
import { Card } from "../../../../components";
import { useTopikMotion } from "../topikMotion";
import { useDraft, draftKey } from "../useDraft";
import { commitHaptic } from "../haptics";
import TextAreaInput from "../../../../components/form/TextAreaInput";
import CharCounter from "../../../../components/form/CharCounter";
import AnalysisFrame from "./AnalysisFrame";
import TopikAssistPanel from "./TopikAssistPanel";

// E10: a small starter set - "borrow a first sentence, keep it or break it". Kept to three
// deliberately distinct angles (synthesis / tension / inquiry) so the block stays light and
// the AI companion below is the escalation, not a sixth chip.
const STARTER_KEYS = ["both", "tension", "question"];

const TopikObservationView = ({
  t, session, chosenNegativeCard, chosenPositiveCard, topikStatus, gameClosed, dispatch,
}) => {
  // Prefill from the payload so a backtrack lands on the existing text (§6.3); a local draft
  // persists across reload/rejoin (§B6) and wins over the payload while it exists.
  const [observation, setObservation] = useDraft(
    draftKey(session, "observation"), session.observation || "",
  );
  const maxLength = session.maxTextLength;
  // The two finalists settle into the writing surface with a calm entrance - the "Turn"
  // downshift from picking to reflecting (§1.2-4, A4). Reduced-motion reveals them at rest.
  const { reduce, calm } = useTopikMotion();
  const titleId = useRef(uniqueId("topik-observation-title-")).current;
  const instructionId = useRef(uniqueId("topik-observation-instruction-")).current;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!observation.trim() || topikStatus.submitting || gameClosed) return;
    commitHaptic();
    dispatch(topikActions.analysis.observation.request({ observation: observation.trim() }));
  };

  const insertStarter = (text) => {
    setObservation((current) => current || text);
  };

  return (
    <AnalysisFrame
      t={t}
      title={t("topik-observation-view.title")}
      titleId={titleId}
      instruction={t("topik-observation-view.instruction")}
      instructionId={instructionId}
      submitting={topikStatus.submitting}
      gameClosed={gameClosed}
      error={topikStatus.error}
    >
      <motion.div
        className="topik-view__chosen-pair"
        initial={reduce ? false : { opacity: 0, y: 14 }}
        animate={{ opacity: 1, y: 0 }}
        transition={reduce ? { duration: 0 } : { ...calm, delay: 0.1 }}
      >
        <div className="topik-view__chosen-card-wrapper">
          <span className="topik-analysis__context-label topik-analysis__context-label--negative">
            {t("topik-observation-view.negative-label")}
          </span>
          <span className="topik-analysis__context-direction topik-analysis__context-direction--negative">
            <span aria-hidden="true">←</span>
            {" "}
            {t("topik-observation-view.negative-direction")}
          </span>
          <Card
            cardId={`ctx-neg-${chosenNegativeCard.id}`}
            title={chosenNegativeCard.title}
            description={chosenNegativeCard.description || ""}
            suit="negative"
          />
        </div>
        <div className="topik-view__chosen-card-wrapper">
          <span className="topik-analysis__context-label topik-analysis__context-label--positive">
            {t("topik-observation-view.positive-label")}
          </span>
          <span className="topik-analysis__context-direction topik-analysis__context-direction--positive">
            {t("topik-observation-view.positive-direction")}
            {" "}
            <span aria-hidden="true">→</span>
          </span>
          <Card
            cardId={`ctx-pos-${chosenPositiveCard.id}`}
            title={chosenPositiveCard.title}
            description={chosenPositiveCard.description || ""}
            suit="positive"
          />
        </div>
      </motion.div>

      <p className="topik-analysis__reunion">{t("topik-observation-view.reunion")}</p>
      <p className="topik-analysis__no-wrong-answer">{t("topik-observation-view.no-wrong-answer")}</p>

      <p className="topik-analysis__starters-framing">{t("topik-observation-view.starters-framing")}</p>
      <div className="topik-analysis__starters">
        {STARTER_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            className="topik-analysis__starter"
            onClick={() => insertStarter(t(`topik-observation-view.starters.${key}`))}
          >
            {t(`topik-observation-view.starters.${key}`)}
          </button>
        ))}
      </div>

      {/* The graduated AI companion (§6.5) - inline, below the free starters it escalates from. */}
      <TopikAssistPanel step="observation" draft={observation} />

      <form className="topik-view__form" onSubmit={handleSubmit}>
        <div className="topik-analysis__textarea-wrapper">
          <TextAreaInput
            className="topik-textarea"
            value={observation}
            onChange={(e) => setObservation(e.target.value)}
            placeholder={t("topik-observation-view.placeholder")}
            minRows={4}
            maxLength={maxLength}
            inputProps={{
              disabled: topikStatus.submitting,
              "aria-labelledby": titleId,
              "aria-describedby": instructionId,
            }}
          />
          <CharCounter value={observation} maxLength={maxLength} nudge={t("topik-view.counter-nudge")} />
        </div>
        <button
          type="submit"
          className="topik-view__submit-button"
          disabled={!observation.trim() || topikStatus.submitting || gameClosed}
        >
          {t("topik-observation-view.submit")}
        </button>
      </form>
    </AnalysisFrame>
  );
};

TopikObservationView.propTypes = {
  t: PropTypes.func.isRequired,
  session: PropTypes.object.isRequired,
  chosenNegativeCard: PropTypes.object.isRequired,
  chosenPositiveCard: PropTypes.object.isRequired,
  topikStatus: PropTypes.object.isRequired,
  gameClosed: PropTypes.bool,
  dispatch: PropTypes.func.isRequired,
};

TopikObservationView.defaultProps = {
  gameClosed: false,
};

const mapStateToProps = (state) => ({
  session: state.topik.session,
  chosenNegativeCard: state.topik.session.chosenNegativeCard,
  chosenPositiveCard: state.topik.session.chosenPositiveCard,
  topikStatus: state.topik.status,
});

export default compose(withTranslation("views"), connect(mapStateToProps))(TopikObservationView);
